import * as z from 'zod';

import * as api from '@gitbook/api-client';
import { SpaceEditMode } from '@gitbook/api-client';
import { createHTTPError } from '@gitbook/util-debug';

import { OrderIndexSchema } from './collections';
import { ContentSharingSchema, URLSchema } from './content';
import { Timestamp } from './db/firestore';
import { UserContributionsSchema } from './helpers';
import { IntegrationInstallationMapSchema } from './integrations/installations';

export const SPACE_TITLE_LIMIT = 50;

export const SPACE_DEFAULT_TITLE = 'Untitled';

/**
 * Git sync properties, declared here to prevent a dependency cycle.
 */
export const GitSyncProviderTypeGitHubSchema = z.literal(api.GitSyncProvider.Github);
export const GitSyncProviderTypeGitLabSchema = z.literal(api.GitSyncProvider.Gitlab);
export const GitSyncProviderTypeGitHubLegacySchema = z.literal(api.GitSyncProvider.GithubLegacy);
export const GitSyncProviderTypeSchema = z.union([
    GitSyncProviderTypeGitHubSchema,
    GitSyncProviderTypeGitLabSchema,
    GitSyncProviderTypeGitHubLegacySchema,
]);
export type GitSyncProviderType = z.infer<typeof GitSyncProviderTypeSchema>;

export const GitSyncOperationImportSchema = z.object({
    id: z.string(),
    direction: z.literal('import'),
    importCommit: z.string(),
});
export type GitSyncOperationImport = z.infer<typeof GitSyncOperationImportSchema>;

export const GitSyncOperationExportSchema = z.object({
    id: z.string(),
    direction: z.literal('export'),
    workingExportRef: z.string(),
    exportCommit: z.string().optional(),
});
export type GitSyncOperationExport = z.infer<typeof GitSyncOperationExportSchema>;

/**
 * State of a gitsync operation stored on the space object.
 */
export const GitSyncOperationStateSchema = z.enum(['running', 'failure', 'success']);
export type GitSyncOperationState = z.infer<typeof GitSyncOperationStateSchema>;

export const GitSyncOperationSchema = z
    .object({
        state: GitSyncOperationStateSchema,
        startedAt: z.instanceof(Timestamp).optional(),
        completedAt: z.instanceof(Timestamp).optional(),
        error: z.string().optional(),
    })
    .and(z.union([GitSyncOperationImportSchema, GitSyncOperationExportSchema]));
export type GitSyncOperation = z.infer<typeof GitSyncOperationSchema>;

/**
 * Reason when a space is blocked via the backoffice
 */
export const SpaceBlockBackOfficeReasonSchema = z.enum(['DMCA']);
export type SpaceBlockBackOfficeReason = z.infer<typeof SpaceBlockBackOfficeReasonSchema>;

/**
 * Internal space categories stored in Firestore.
 */
export const SpaceType = {
    Default: 'default',
    Styleguide: 'styleguide',
};
export type SpaceType = (typeof SpaceType)[keyof typeof SpaceType];

export const SpaceSchema = ContentSharingSchema.extend({
    key: z.string(),
    title: z.string().max(SPACE_TITLE_LIMIT),
    /**
     * Type of space. Defaults to `default` when unset.
     */
    type: z.enum([SpaceType.Default, SpaceType.Styleguide]).optional(),
    /**
     * Index to order the space with others.
     */
    orderIndex: OrderIndexSchema,
    /**
     * Selected emoji for this space, if one has been selected.
     */
    emoji: z.string().optional(),
    /**
     * The url for a custom icon for this space, if one has been selected.
     */
    iconURL: URLSchema.optional(),
    /**
     * Language of the content in this space.
     * We use the prop name "contentLanguage" as "language" was used by the now deprecated property (before sites)
     */
    contentLanguage: z.enum(api.TranslationLanguage).optional(),
    /**
     * Comments per users on this space.
     */
    comments: UserContributionsSchema,
    /**
     * Last revision where content refs were indexed
     * @deprecated We have to keep this around until we get rid of backend-content-references/v1
     * No need to migrate to PlanetScale.
     */
    contentRefIndexedRevision: z.string().optional(),
    /**
     * Timestamp of when the space was blocked.
     * Undefined if it's not blocked.
     * (Boolean for older records, still supported for backward compatibility.)
     */
    blocked: z.union([z.boolean(), z.instanceof(Timestamp)]).optional(),
    /**
     * Number of comments on this space (non resolved).
     */
    openComments: z.number().optional(),
    /**
     * Total number of change requests on this space.
     */
    changeRequests: z.number().optional(),
    /**
     * Number of change requests on this space (in open state).
     */
    openChangeRequests: z.number().optional(),
    /**
     * Number of change requests on this space (in draft state).
     */
    draftChangeRequests: z.number().optional(),
    /**
     * When defined, the space has been soft-deleted.
     * It'll be hard-deleted 7-days after this date.
     */
    deletedAt: z.instanceof(Timestamp).optional(),
    /**
     * When defined, indicates the user who soft-deleted the space.
     */
    deletedBy: z.string().optional(),
    /**
     * When this space was created
     */
    createdAt: z.instanceof(Timestamp),
    /**
     * The user who created this space
     */
    createdBy: z.string(),
    /**
     * List of integrations installed on this space
     */
    integrations: IntegrationInstallationMapSchema.optional(),
    /**
     * Merge rule for this space
     */
    mergeRules: z.custom<api.MergeRulesSpaceConfiguration>().optional(),
    /**
     * How should this space be edited ?
     */
    editMode: z.enum(SpaceEditMode),
    /**
     * Last repository tree URL this space synced with through Git Sync, cached so it is still known
     * once its Git Sync installation is removed. Only authoritative when the space has no
     * installation; while one exists, read the URL from it.
     *
     * Only ever written to MySQL, and never reconciled from Firestore.
     */
    cachedGitSyncRepoProjectURL: z.string().nullable().optional(),
});

export type Space = z.infer<typeof SpaceSchema>;

/**
 * A space along with the deprecated `revision` field that still exists on raw space documents.
 * The Hive V2 runtime keeps the field in sync for backward compatibility (see LegacyFirestore
 * in services/hive/runtime), and Hive V1 test fixtures still rely on it.
 *
 * New code should NOT use this type: fetch the current revision from Hive instead (see querySpace
 * and getMainBranchRevision). It exists only for the few remaining call sites that read the field
 * straight off the raw document.
 *
 * TODO-REMOVE-LEGACY-SPACE-REVISION
 * Remove this type (and stop writing the field) once all of the following are done:
 * - Hive V1 is fully purged from all environments, so the V1 fallbacks (and test fixtures) that read `revision` off the raw document are no longer needed.
 */
export type SpaceWithLegacyRevision = Space & { revision: string };

/**
 * Normalize a space title.
 */
export function normalizeSpaceTitle(title: string): string {
    return title
        .replace(/(\r\n|\r|\n)/g, ' ')
        .trim()
        .slice(0, SPACE_TITLE_LIMIT)
        .trim();
}

/**
 * Number of days before a soft-deleted space is hard-deleted.
 */
export const SPACE_SOFT_DELETE_DAYS = 7;

/**
 * Return true if the space is soft-deleted.
 */
export function isSoftDeletedSpace(space: Pick<Space | api.Space, 'deletedAt'>): boolean {
    return Boolean(space.deletedAt);
}

/**
 * Assert the current space is active and not deleted.
 */
export function assertSpaceNotDeleted(space: Space) {
    if (isSoftDeletedSpace(space)) {
        throw new createHTTPError.NotFound('Space has been deleted');
    }
}

/**
 * Map a site visibility to a content visibility.
 */
export function siteToContentVisibility(site: api.SiteVisibility): api.ContentVisibility {
    switch (site) {
        case api.SiteVisibility.Public:
            return api.ContentVisibility.Public;
        case api.SiteVisibility.Unlisted:
            return api.ContentVisibility.Unlisted;
        case api.SiteVisibility.ShareLink:
            return api.ContentVisibility.ShareLink;
        case api.SiteVisibility.VisitorAuth:
            return api.ContentVisibility.VisitorAuth;
    }
}
